import { ThreadGroup, Thread } from '../interfaces/copilotChat';
import { Result, ChangeRequestGroup } from '../interfaces/changeRequest';

type GroupKey = 'today' | 'week' | 'month' | string;

interface GroupBase<T> {
  key: GroupKey;
  label: string;
  items: T[];
}

type GetUpdatedAt<T> = (item: T) => string;

const groupByDate = <T>(
  items: T[],
  getUpdatedAt: GetUpdatedAt<T>
): GroupBase<T>[] => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 7);

  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(today.getDate() - 30);

  const currentYear = today.getFullYear();
  const groups: GroupBase<T>[] = [];

  items.forEach((item) => {
    const updatedAt = new Date(getUpdatedAt(item));
    const itemDate = new Date(updatedAt);
    itemDate.setHours(0, 0, 0, 0);

    const addToGroup = (key: GroupKey, label: string) => {
      const group = groups.find((g) => g.key === key);
      if (group) {
        group.items.push(item);
      } else {
        groups.push({
          key,
          label,
          items: [item],
        });
      }
    };

    if (itemDate.getTime() === today.getTime()) {
      return addToGroup('today', 'Today');
    }

    if (itemDate > sevenDaysAgo && itemDate < today) {
      return addToGroup('week', 'Previous 7 Days');
    }

    if (itemDate > thirtyDaysAgo && itemDate <= sevenDaysAgo) {
      return addToGroup('month', 'Previous 30 Days');
    }

    if (updatedAt.getFullYear() === currentYear) {
      const monthName = updatedAt.toLocaleString('default', { month: 'long' });
      const monthKey = `month-${updatedAt.getMonth()}`;
      return addToGroup(monthKey, monthName);
    }

    const year = updatedAt.getFullYear().toString();
    return addToGroup(`year-${year}`, year);
  });

  return groups;
};

export const getThreadGroups = (threads: Thread[]): ThreadGroup[] => {
  return groupByDate(threads, (t) => t.updated_at).map((group) => ({
    key: group.key,
    label: group.label,
    threads: group.items,
  }));
};

export const getChangeRequestGroups = (
  changeRequests: Result[]
): ChangeRequestGroup[] => {
  return groupByDate(changeRequests, (r) => r.last_active_at).map((group) => ({
    key: group.key,
    label: group.label,
    changeRequests: group.items,
  }));
};
